import SplashScreen from '@common/components/SplashScreen';
import { isLinkSlug, isPromoCode, landingPath, storePromoClick, storePromoCode, visitorId } from '@common/helpers/promoLink/promoLink';
import { APP_ADDS_CLICK_ID, APP_KEITARO_SUBID, APP_LCC } from '@common/constants';
import { handleAffiliateParams } from '@common/helpers/affiliate/handleAffiliateParams';
import { storeIlgsParams } from '@common/helpers/ilgs/ilgs';
import { storeFbclid } from '@common/helpers/metaCapi/metaCapi';
import { handleMiniGamesParams } from '@common/helpers/miniGames';
import { useAppDispatch } from '@common/hooks/store';
import '@common/utils/yupLocale';
import config from '@config';
import { useFetchTooltipsQuery } from '@features/app/api/appApi';
import AppLoader from '@features/app/AppLoader';
import useCheckToken from '@features/auth/useCheckToken';
import { campaignsApi } from '@features/campaigns/api/campaignsApi';
import usePromoRewardRefresh from '@features/promo-code/usePromoRewardRefresh';
import { getUnixTime } from 'date-fns';
import React, { useEffect } from 'react';
import ReactGA from 'react-ga4';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { reactLocalStorage } from 'reactjs-localstorage';

export default function App() {
  ReactGA.initialize(config.googleAnalyticsID);

  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [ searchParams ] = useSearchParams();
  const isChecking = useCheckToken();
  const refreshRewards = usePromoRewardRefresh();

  const tooltipsQuery = useFetchTooltipsQuery(undefined, {
    skip: isChecking,
  });

  useEffect(() => {
    handleAffiliateParams(searchParams, dispatch, navigate);
    handleMiniGamesParams(searchParams);
  }, [ dispatch, searchParams, navigate ]);

  useEffect(() => {
    storeIlgsParams(searchParams);
    storeFbclid(searchParams);
    const yandexClickId = searchParams.get('yclid');
    const facebookClickId = searchParams.get('fbclid');
    const googleClickId = searchParams.get('_gl');
    if (yandexClickId) {
      reactLocalStorage.set(APP_ADDS_CLICK_ID, `Yandex:${yandexClickId}`);
    } else if (facebookClickId) {
      reactLocalStorage.set(APP_ADDS_CLICK_ID, `Facebook:${facebookClickId}`);
    } else if (googleClickId) {
      reactLocalStorage.set(APP_ADDS_CLICK_ID, `Google:${googleClickId}`);
    }
  }, [ searchParams ]);

  useEffect(() => {
    const subid = searchParams.get('subid');
    if (subid) {
      const unixTime = getUnixTime(new Date());
      reactLocalStorage.setObject(APP_KEITARO_SUBID, {
        exp: unixTime + (60 * 30),
        code: subid,
      });
      dispatch(campaignsApi.endpoints.leadCampaigns.initiate(subid));
      navigate(location.pathname);
    }
    const lcc = searchParams.get('lcc');
    if (lcc) {
      const unixTime = getUnixTime(new Date());
      reactLocalStorage.setObject(APP_LCC, {
        exp: unixTime + (60 * 30),
        code: lcc,
      });
      dispatch(campaignsApi.endpoints.leadCampaigns.initiate(lcc));
      navigate(location.pathname);
    }
    const promoCode = searchParams.get('promo_code') || searchParams.get('promo');
    const promoLink = searchParams.get('plk');
    if (isPromoCode(promoCode)) {
      storePromoCode(promoCode);
    }
    if (isLinkSlug(promoLink)) {
      dispatch(campaignsApi.endpoints.promoLinkClick.initiate({ link: promoLink, visitor: visitorId() })).unwrap()
        .then((click) => {
          storePromoClick(click.clickId, click.promoCode);
          if (!isPromoCode(promoCode) && isPromoCode(click.promoCode)) {
            storePromoCode(click.promoCode);
          }
          if (click.joined) {
            refreshRewards();
            navigate(landingPath(click.landing));
          }
        })
        .catch(() => undefined);
    }
    if (promoCode || promoLink) {
      navigate(location.pathname);
    }
  }, [ dispatch, location.pathname, navigate, refreshRewards, searchParams ]);

  if (
    isChecking ||
    tooltipsQuery.isUninitialized ||
    tooltipsQuery.isLoading
  ) {
    return <SplashScreen/>;
  }
  return <AppLoader/>;
}
